import {
  ComponentPropsWithRef,
  CSSProperties,
  FocusEventHandler,
  ReactElement,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react"

import { classNames } from "../../../../../lib/classNames"
import { Icon, IconProps, IconSizes } from "../../../../Icon"
import { Label, LabelProps } from "../../../informational/Label"

export const TextInputVariants = {
  DEFAULT: "default",
  SUBTLE: "subtle",
} as const
export type TextInputVariant =
  (typeof TextInputVariants)[keyof typeof TextInputVariants]

export const TextInputSizes = {
  SMALL: "sm",
  MEDIUM: "md",
} as const
export type TextInputSize = (typeof TextInputSizes)[keyof typeof TextInputSizes]

export interface TextInputProps
  extends ComponentPropsWithRef<"input">,
    LabelProps {
  variant?: TextInputVariant
  inputSize?: TextInputSize
  invalid?: boolean
  selectOnFocus?: boolean
  leftIcon?: IconProps["icon"]
  leftText?: string
  rightText?: string
  rightIcon?: IconProps["icon"]
  rightElement?: ReactElement
}

export const TextInput = ({
  variant = TextInputVariants.DEFAULT,
  inputSize = TextInputSizes.MEDIUM,
  label,
  secondaryLabel,
  subLabel,
  infoMessage,
  className,
  invalid = false,
  errorMessage,
  leftIcon,
  leftText,
  rightText,
  rightIcon,
  rightElement,
  containerClassName,
  onFocus,
  selectOnFocus = true,
  ref,
  ...otherProps
}: TextInputProps) => {
  const id = useId()

  const leftContainerRef = useRef<HTMLDivElement>(null)
  const [leftContainerWidth, setLeftContainerWidth] = useState(0)
  useEffect(() => {
    if (!leftContainerRef.current) return
    setLeftContainerWidth(leftContainerRef.current.offsetWidth)
  }, [leftText, leftIcon])

  const rightContainerRef = useRef<HTMLDivElement>(null)
  const [rightContainerWidth, setRightContainerWidth] = useState(0)
  useEffect(() => {
    if (!rightContainerRef.current) return
    setRightContainerWidth(rightContainerRef.current.offsetWidth)
  }, [rightText, rightIcon])

  const inputStyle: CSSProperties = useMemo(() => {
    const style: CSSProperties = {}
    if (leftContainerWidth) {
      style.paddingLeft = leftContainerWidth
    }
    if (rightContainerWidth) {
      style.paddingRight = rightContainerWidth
    }
    return style
  }, [leftContainerWidth, rightContainerWidth])

  const handleFocus: FocusEventHandler<HTMLInputElement> = (e) => {
    if (selectOnFocus) {
      e.target.select()
    }
    onFocus?.(e)
  }

  const fieldError = invalid || !!errorMessage

  return (
    <Label
      label={label}
      subLabel={subLabel}
      secondaryLabel={secondaryLabel}
      infoMessage={infoMessage}
      errorMessage={errorMessage}
      inputId={otherProps.id ?? id}
      containerClassName={containerClassName}
    >
      <div
        className={classNames(
          "group flex items-center",
          (leftIcon || leftText || rightIcon || rightText || rightElement) &&
            "relative"
        )}
      >
        {(leftIcon || leftText) && (
          <div
            className="absolute inset-y-0 left-0 flex items-center gap-x-2 pl-3 pr-2"
            ref={leftContainerRef}
          >
            {leftIcon && (
              <Icon
                icon={leftIcon}
                size={IconSizes.ExtraSmall}
                className={classNames(
                  "color-icon-subtle",
                  "group-has-disabled:color-icon-disabled"
                )}
              />
            )}
            {leftText && (
              <span
                className={classNames(
                  "color-subtler text-body",
                  "group-has-disabled:color-disabled"
                )}
              >
                {leftText}
              </span>
            )}
          </div>
        )}
        <input
          ref={ref}
          type="text"
          aria-invalid={invalid}
          className={classNames(
            // color-subtler's values, set directly: the `placeholder:` variant
            // doesn't compose with the dark variant baked into color-subtler,
            // so the dark color is applied explicitly here.
            "text-body disabled:color-disabled rounded-control h-10 w-full placeholder:text-gray-500 dark:placeholder:text-gray-400",
            variant === TextInputVariants.SUBTLE
              ? "border-none bg-transparent focus:ring-0 focus-visible:ring-0"
              : "border-v2-border-default bg-input-default shadow-control hover:not-disabled:border-v2-border-strong disabled:border-v2-border-default-disabled disabled:bg-input-disabled",
            inputSize === TextInputSizes.SMALL && "h-7.5",
            fieldError &&
              "border-v2-border-danger hover:not-disabled:border-v2-border-danger border-2",
            className
          )}
          onFocus={handleFocus}
          id={otherProps.id ?? id}
          style={inputStyle}
          {...otherProps}
        />
        {(rightText || rightIcon || rightElement) && (
          <div
            className="absolute inset-y-0 right-0 flex items-center gap-x-2 pl-2 pr-3"
            ref={rightContainerRef}
          >
            {rightText && (
              <span
                className={classNames(
                  "color-subtler text-body",
                  "group-has-disabled:color-disabled"
                )}
              >
                {rightText}
              </span>
            )}
            {rightIcon && (
              <Icon
                icon={rightIcon}
                size={IconSizes.ExtraSmall}
                className={classNames(
                  "color-icon-subtle",
                  "group-has-disabled:color-icon-disabled"
                )}
              />
            )}
            {rightElement}
          </div>
        )}
      </div>
    </Label>
  )
}
